import { Stack, Text } from "@mantine/core";
import { ArrowLeft } from "lucide-react";
import { useState, type FormEvent } from "react";

import { isApiError } from "../../api/errors";
import {
  useRequestSignInCode,
  useVerifySignInCode,
} from "../../api/generated/endpoints/identity/identity";
import { EmailCodeRequest, EmailCodeVerify } from "../../api/generated/zod";
import { ErrorAlert } from "../../platform/ErrorAlert";
import { browserTimeZone } from "../../platform/time-zone";
import { AuthField, AuthPrimaryButton, AuthSecondaryButton } from "../../ui/AuthShell";
import { NotInvitedNotice, UseAnotherEmailButton } from "./NotInvitedNotice";

// The steps live in this component's state only: never in storage, so nothing survives a reload
// or a sign-out (audit frontend 2.4, B.9).
type Step =
  | { name: "email"; email: string }
  | { name: "code"; email: string; expiresInSeconds: number }
  | { name: "not-invited"; email: string };

export function EmailCodeForm({ onSignedIn }: { onSignedIn: () => void }) {
  const [step, setStep] = useState<Step>({ name: "email", email: "" });
  const restart = () => setStep({ name: "email", email: step.email });
  if (step.name === "email") {
    return (
      <EmailStep
        initialEmail={step.email}
        onSent={(email, expiresInSeconds) => setStep({ name: "code", email, expiresInSeconds })}
      />
    );
  }
  if (step.name === "not-invited") {
    return (
      <NotInvitedNotice email={step.email} action={<UseAnotherEmailButton onClick={restart} />} />
    );
  }
  return (
    <CodeStep
      email={step.email}
      expiresInSeconds={step.expiresInSeconds}
      onBack={restart}
      onSignedIn={onSignedIn}
      onNotInvited={() => setStep({ name: "not-invited", email: step.email })}
    />
  );
}

interface EmailStepProps {
  initialEmail: string;
  onSent: (email: string, expiresInSeconds: number) => void;
}

function EmailStep({ initialEmail, onSent }: EmailStepProps) {
  const [email, setEmail] = useState(initialEmail);
  const [invalid, setInvalid] = useState(false);
  const request = useRequestSignInCode();
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const parsed = EmailCodeRequest.safeParse({ email: email.trim() });
    setInvalid(!parsed.success);
    if (!parsed.success) return;
    request.mutate(
      { data: parsed.data },
      { onSuccess: (sent) => onSent(parsed.data.email, sent.expires_in_seconds) },
    );
  };
  return (
    <form onSubmit={submit} noValidate>
      <Stack gap={24}>
        <AuthField
          label="Email"
          type="email"
          autoComplete="email"
          placeholder="Enter your email"
          value={email}
          onChange={(event) => setEmail(event.currentTarget.value)}
          error={invalid ? "Enter an email address, such as name@example.com." : undefined}
        />
        {request.isError ? <ErrorAlert error={request.error} /> : null}
        <AuthPrimaryButton type="submit" loading={request.isPending}>
          Email me a sign-in code
        </AuthPrimaryButton>
      </Stack>
    </form>
  );
}

interface CodeStepProps {
  email: string;
  expiresInSeconds: number;
  onBack: () => void;
  onSignedIn: () => void;
  onNotInvited: () => void;
}

function CodeStep({ email, expiresInSeconds, onBack, onSignedIn, onNotInvited }: CodeStepProps) {
  const [code, setCode] = useState("");
  const [invalid, setInvalid] = useState(false);
  const verify = useVerifySignInCode();
  const resend = useRequestSignInCode();
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const parsed = EmailCodeVerify.safeParse({
      email,
      code: code.trim(),
      time_zone: browserTimeZone(),
    });
    setInvalid(!parsed.success);
    if (!parsed.success) return;
    resend.reset();
    verify.mutate(
      { data: parsed.data },
      {
        onSuccess: onSignedIn,
        onError: (error) => (isApiError(error, "NOT_INVITED") ? onNotInvited() : setCode("")),
      },
    );
  };
  const sendAgain = () => {
    verify.reset();
    resend.mutate({ data: { email } });
  };
  const minutes = Math.max(1, Math.round(expiresInSeconds / 60));
  return (
    <form onSubmit={submit} noValidate>
      <Stack gap={24}>
        <Text size="sm">
          We sent a 6-digit code to <strong>{email}</strong>. It works for {minutes} minutes.
        </Text>
        <AuthField
          label="Sign-in code"
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={6}
          placeholder="Enter the 6 digits"
          value={code}
          onChange={(event) => setCode(event.currentTarget.value)}
          error={invalid ? "Enter the 6 digits from the email." : undefined}
        />
        {verify.isError ? <ErrorAlert error={verify.error} /> : null}
        {resend.isError ? <ErrorAlert error={resend.error} /> : null}
        {/* An <output> is a status region, so a screen reader hears that a code went out. */}
        {resend.isSuccess ? (
          <Text component="output" size="sm">
            We sent a new code to {email}.
          </Text>
        ) : null}
        <AuthPrimaryButton type="submit" loading={verify.isPending}>
          Sign in
        </AuthPrimaryButton>
        <Stack gap={12}>
          <AuthSecondaryButton leftSection={<ArrowLeft size={20} />} onClick={onBack}>
            Use a different email
          </AuthSecondaryButton>
          <AuthSecondaryButton onClick={sendAgain} loading={resend.isPending}>
            Send a new code
          </AuthSecondaryButton>
        </Stack>
      </Stack>
    </form>
  );
}
